import type { MouseEvent as ReactMouseEvent } from 'react';

import { isTrelloUrl } from '@trello/urls';

import { fileLinkHandler } from './fileLinkHandler';
import { isModifierKeyPressed } from './isModifierKeyPressed';
import { trelloLinkHandler } from './trelloLinkHandler';

/**
 * Since we allow users to add links in descriptions, comments, checkitems,
 * etc., etc., we can pass click events through this function to ensure they
 * get handled correctly. Trello links should be navigated to using the router,
 * external links get opened in a new tab, and for file:// links, we show the
 * user a message in a popover instructing them to copy the url and open it
 * manually.
 *
 * @param event the click event
 * @param linkElement the link element that was clicked
 * @param target (optional) target for external links; defaults to "_blank"
 * @returns undefined
 */
export const userGeneratedLinkHandler = (
  event: MouseEvent | ReactMouseEvent,
  linkElement: HTMLAnchorElement,
  target: string = '_blank',
  attachmentLinkCallback?: (attachmentId: string) => void,
) => {
  const url = linkElement.href;
  const shouldNavigateInApp = isTrelloUrl(url) && !isModifierKeyPressed(event);

  if (shouldNavigateInApp) {
    const attachmentRegex =
      /^.*\/1\/cards\/[^/]+\/attachments\/([^/]+)\/download\/[^/]+$/;
    const match = url.match(attachmentRegex);
    if (match) {
      event.preventDefault();
      attachmentLinkCallback?.(match[1]);
      return;
    } else {
      linkElement.target = '';
      trelloLinkHandler(event, linkElement);
    }
  } else if (url.startsWith('file://')) {
    fileLinkHandler(event, linkElement);
  } else {
    event.preventDefault();
    window.open(url, target, 'noopener, noreferrer');
  }
};
